import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import SingleSelect from 'components/singleSelect/SingleSelect';
import { api } from 'api';
import { routes } from 'common';
import { useDispatch, useSelector } from 'react-redux';
import { generalExtraActions } from '../../reduxStore/actions';
import { Loader } from 'components';
import '../grounds/_groundCreate.scss';
import * as Yup from 'yup';

const maintenanceValidationSchema = Yup.object().shape({
  ground_id: Yup.string().required('Ground is required'),
  ground_field_id: Yup.string().nullable(),
  start_date: Yup.date().required('Start date is required'),
  end_date: Yup.date()
    .required('End date is required')
    .min(Yup.ref('start_date'), 'End date must be after or equal to start date'),
  is_whole_day: Yup.boolean(),
  start_time: Yup.string().when('is_whole_day', {
    is: false,
    then: (schema) => schema.required('Start time is required'),
    otherwise: (schema) => schema.nullable(),
  }),
  end_time: Yup.string().when('is_whole_day', {
    is: false,
    then: (schema) => schema.required('End time is required'),
    otherwise: (schema) => schema.nullable(),
  }),
  reason: Yup.string(),
});

function MaintenanceCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'create';
  const { grounds } = useSelector((state) => state.general);
  const { user } = useSelector((state) => state.auth);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [initialValues, setInitialValues] = useState({
    ground_id: '',
    ground_field_id: '',
    start_date: '',
    end_date: '',
    is_whole_day: true,
    start_time: '',
    end_time: '',
    reason: '',
  });

  useEffect(() => {
    // For ground admins, only fetch their own grounds
    dispatch(generalExtraActions.getGrounds());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && id) {
      fetchMaintenanceDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchMaintenanceDetail = async () => {
    try {
      setLoading(true);
      const data = await api.getMaintenanceDetail(id);
      setInitialValues({
        ground_id: data.ground?.id?.toString() || '',
        ground_field_id: data.ground_field?.id?.toString() || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        is_whole_day: data.is_whole_day ?? true,
        start_time: data.start_time || '',
        end_time: data.end_time || '',
        reason: data.reason || '',
      });

      // Fetch fields if ground is selected
      if (data.ground?.id) {
        await fetchFieldsForGround(data.ground.id);
      }
    } catch (error) {
      toast.error('Failed to load maintenance details');
      navigate(routes.MAINTENANCE);
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldsForGround = async (groundId) => {
    try {
      const response = await api.getFields({ ground: groundId });
      setFields(response?.results || []);
    } catch (error) {
      console.error('Failed to fetch fields:', error);
    }
  };

  const handleGroundChange = (groundId, setFieldValue) => {
    setFieldValue('ground_id', groundId);
    setFieldValue('ground_field_id', ''); // Reset field when ground changes
    if (groundId) {
      fetchFieldsForGround(groundId);
    } else {
      setFields([]);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const payload = {
        ground_id: values.ground_id,
        ground_field_id: values.ground_field_id || null,
        start_date: values.start_date,
        end_date: values.end_date,
        is_whole_day: values.is_whole_day,
        start_time: values.is_whole_day ? null : values.start_time,
        end_time: values.is_whole_day ? null : values.end_time,
        reason: values.reason || '',
      };

      if (isEdit) {
        await api.updateMaintenance(id, payload);
        toast.success('Maintenance updated successfully');
      } else {
        await api.createMaintenance(payload);
        toast.success('Maintenance created successfully');
      }
      navigate(routes.MAINTENANCE);
    } catch (error) {
      const errorMessage = error?.response?.data || error?.message || 'Failed to save maintenance';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to save maintenance');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Filter grounds to show only those owned by the current user
  const userGrounds = grounds?.filter(ground => {
    // For ground admins, backend already filters, but we can add extra safety
    return true; // Backend already filters by owner
  }) || [];
  
  const groundOptions = userGrounds?.map((g) => ({ label: g.name, value: g.id.toString() })) || [];

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this maintenance period? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await api.deleteMaintenance(id);
      toast.success('Maintenance deleted successfully');
      navigate(routes.MAINTENANCE);
    } catch (error) {
      toast.error('Failed to delete maintenance');
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (loading && isEdit) {
    return <Loader />;
  }

  return (
    <div className="field-create-page ground-detail-page detail-page">
      {loading && <Loader />}
      <div className="header">
        <h1>{isEdit ? 'Edit Maintenance' : 'Create Maintenance'}</h1>
        <p className="subtitle">
          {isEdit
            ? 'Update maintenance period for ground or field'
            : 'Schedule maintenance period for ground or field'}
        </p>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={maintenanceValidationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          // Filter fields to only show those that belong to the selected ground
          const fieldOptions = fields
            ?.filter((f) => {
              // If no ground is selected, don't show any fields
              if (!values.ground_id) return false;
              
              // Check if field belongs to the selected ground
              const fieldGroundId = typeof f.ground === 'object' ? f.ground?.id?.toString() : f.ground?.toString();
              return fieldGroundId === values.ground_id.toString();
            })
            ?.map((f) => ({ label: f.name, value: f.id.toString() })) || [];

          return (
            <Form className="custom-form">
              <div className="basic-info-content">
                <h3>Maintenance Details</h3>

                <div className="form-group">
                  <label htmlFor="ground_id">
                    Ground <span className="required">*</span>
                  </label>
                  <SingleSelect
                    options={groundOptions}
                    value={values.ground_id}
                    onChange={(val) => handleGroundChange(val, setFieldValue)}
                    placeholder="Select a ground"
                    searchable={true}
                  />
                  <ErrorMessage name="ground_id" component="div" className="error-message" />
                  <small className="form-text text-muted">
                    Select the ground for maintenance. If no field is selected, maintenance applies to all fields in this ground.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="ground_field_id">Field (Optional - for field-specific maintenance)</label>
                  <SingleSelect
                    options={fieldOptions}
                    value={values.ground_field_id}
                    onChange={(val) => setFieldValue('ground_field_id', val)}
                    placeholder={
                      values.ground_id
                        ? fieldOptions.length > 0
                          ? 'Select a field (optional - leave empty for all fields)'
                          : 'No fields available for this ground'
                        : 'Select a ground first'
                    }
                    isDisabled={!values.ground_id}
                    searchable={false}
                  />
                <ErrorMessage name="ground_field_id" component="div" className="error-message" />
                <small className="form-text text-muted">
                  Optional: Select a specific field for maintenance. If left empty, maintenance applies to all fields in the selected ground.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="start_date">
                  Start Date <span className="required">*</span>
                </label>
                <Field type="date" name="start_date" className="form-control" />
                <ErrorMessage name="start_date" component="div" className="error-message" />
              </div>

              <div className="form-group">
                <label htmlFor="end_date">
                  End Date <span className="required">*</span>
                </label>
                <Field type="date" name="end_date" className="form-control" min={values.start_date} />
                <ErrorMessage name="end_date" component="div" className="error-message" />
              </div>

              <div className="form-group">
                <label style={{ marginBottom: '8px', display: 'block', fontWeight: '500', color: '#333' }}>
                  Maintenance Type
                </label>
                <div 
                  className="checkbox-wrapper" 
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '16px',
                    backgroundColor: values.is_whole_day ? '#e8f5e8' : '#f8f9fa',
                    borderRadius: '8px',
                    border: `2px solid ${values.is_whole_day ? '#0BAB7C' : '#e0e0e0'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginTop: '8px'
                  }}
                  onClick={() => setFieldValue('is_whole_day', !values.is_whole_day)}
                  onMouseEnter={(e) => {
                    if (!values.is_whole_day) {
                      e.currentTarget.style.borderColor = '#0BAB7C';
                      e.currentTarget.style.backgroundColor = '#f0f9f4';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!values.is_whole_day) {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                >
                  <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
                    <Field 
                      type="checkbox" 
                      name="is_whole_day" 
                      id="is_whole_day"
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: '#0BAB7C',
                        margin: 0
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label 
                      htmlFor="is_whole_day" 
                      style={{
                        margin: 0,
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '15px',
                        color: values.is_whole_day ? '#0BAB7C' : '#333',
                        display: 'block',
                        marginBottom: '6px'
                      }}
                    >
                      Whole Day Maintenance
                    </label>
                    <small className="form-text text-muted" style={{ display: 'block', fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
                      {values.is_whole_day 
                        ? 'Maintenance will apply to the entire day for all time slots.'
                        : 'Specify a time range below. Maintenance will only apply during the selected hours.'}
                    </small>
                  </div>
                </div>
              </div>

              {!values.is_whole_day && (
                <>
                  <div className="form-group">
                    <label htmlFor="start_time">
                      Start Time <span className="required">*</span>
                    </label>
                    <Field type="time" name="start_time" className="form-control" />
                    <ErrorMessage name="start_time" component="div" className="error-message" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="end_time">
                      End Time <span className="required">*</span>
                    </label>
                    <Field type="time" name="end_time" className="form-control" />
                    <ErrorMessage name="end_time" component="div" className="error-message" />
                  </div>
                </>
              )}

              <div className="form-group">
                <label htmlFor="reason">Reason (Optional)</label>
                <Field
                  as="textarea"
                  name="reason"
                  className="form-control"
                  rows="3"
                  placeholder="Enter reason for maintenance..."
                />
                <ErrorMessage name="reason" component="div" className="error-message" />
              </div>
            </div>

            <div className="actions-section">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="submit-btn primary" type="submit" disabled={loading || deleting}>
                  {loading ? 'Saving...' : isEdit ? 'Update Maintenance' : 'Create Maintenance'}
                </button>
                {isEdit && (
                  <button
                    className="submit-btn danger"
                    type="button"
                    onClick={handleDelete}
                    disabled={loading || deleting}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      opacity: deleting ? 0.6 : 1,
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!deleting) {
                        e.currentTarget.style.backgroundColor = '#c82333';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!deleting) {
                        e.currentTarget.style.backgroundColor = '#dc3545';
                      }
                    }}
                  >
                    {deleting ? 'Deleting...' : 'Delete Maintenance'}
                  </button>
                )}
                <button
                  className="submit-btn secondary"
                  type="button"
                  onClick={() => navigate(routes.MAINTENANCE)}
                  disabled={loading || deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default MaintenanceCreate;

